import { TOTAL_QURAN_VERSES, globalAyahNumber } from './quranIndex';
import { readPack, writePack, removePack, isSupported } from './offlineStore';

// The whole text of the Quran, kept on the device.
//
// One request per edition rather than one per verse: /v1/quran/{edition} returns
// the complete text in a single response, so three requests replace the 18,708
// the verse-by-verse endpoint would need.
//
// Text only. Audio is a different order of magnitude — hundreds of megabytes —
// and is not part of this.

const PACK_KEY = 'text-v1';
const PACK_VERSION = 1;

export const ARABIC_EDITION = 'quran-uthmani';
export const TRANSLITERATION_EDITION = 'en.transliteration';

const QURAN_ENDPOINT = 'https://api.alquran.cloud/v1/quran';

/**
 * Flatten one edition's response into an array indexed by global ayah number,
 * so a lookup is a single array access.
 *
 * Throws rather than returning something partial: half a Quran stored as if it
 * were whole would fail silently later, at the verse the reader happened to
 * reach.
 */
export const flattenEdition = (payload) => {
  const surahs = payload?.data?.surahs;
  if (!Array.isArray(surahs)) throw new Error('Unexpected response: no surahs');

  const texts = new Array(TOTAL_QURAN_VERSES).fill(null);
  let seen = 0;

  surahs.forEach((surah) => {
    const ayahs = Array.isArray(surah?.ayahs) ? surah.ayahs : [];
    ayahs.forEach((ayah) => {
      const index = globalAyahNumber(surah.number, ayah.numberInSurah);
      if (!index || typeof ayah.text !== 'string') return;
      texts[index - 1] = ayah.text;
      seen += 1;
    });
  });

  if (seen !== TOTAL_QURAN_VERSES || texts.some((text) => text === null)) {
    throw new Error(`Incomplete edition: ${seen} of ${TOTAL_QURAN_VERSES} verses`);
  }
  return texts;
};

// Surah names as the API gives them, so the heading reads the same offline.
export const extractSurahs = (payload) => {
  const surahs = payload?.data?.surahs;
  if (!Array.isArray(surahs)) return [];
  return surahs.map((surah) => ({
    number: surah.number,
    name: surah.name,
    englishName: surah.englishName,
    numberOfAyahs: Array.isArray(surah.ayahs) ? surah.ayahs.length : 0
  }));
};

const fetchEdition = async (edition, fetchImpl = fetch) => {
  const response = await fetchImpl(`${QURAN_ENDPOINT}/${encodeURIComponent(edition)}`);
  if (!response.ok) throw new Error(`${edition}: server replied ${response.status}`);
  const payload = await response.json();
  return { texts: flattenEdition(payload), surahs: extractSurahs(payload) };
};

export const approximatePackBytes = (pack) => {
  if (!pack) return 0;
  // Two bytes per character is the right order for Arabic in UTF-16, and near
  // enough for a figure shown to a reader.
  const count = (list) => list.reduce((total, text) => total + text.length * 2, 0);
  return count(pack.arabic) + count(pack.translation) + count(pack.transliteration);
};

/**
 * Download and store the three editions.
 * `onProgress` is called with { step, total, label } as each one lands.
 */
export const downloadPack = async ({ translation, onProgress = () => {}, fetchImpl = fetch }) => {
  const steps = [
    { key: 'arabic', edition: ARABIC_EDITION, label: 'Arabic text' },
    { key: 'translation', edition: translation, label: 'Translation' },
    { key: 'transliteration', edition: TRANSLITERATION_EDITION, label: 'Transliteration' }
  ];

  const pack = {
    version: PACK_VERSION,
    editions: { arabic: ARABIC_EDITION, translation, transliteration: TRANSLITERATION_EDITION },
    downloadedAt: new Date().toISOString()
  };

  for (let index = 0; index < steps.length; index += 1) {
    const { key, edition, label } = steps[index];
    onProgress({ step: index + 1, total: steps.length, label });
    // Sequential on purpose: three concurrent multi-megabyte responses on a
    // phone connection is slower, not faster, and harder to report on.
    // eslint-disable-next-line no-await-in-loop
    const { texts, surahs } = await fetchEdition(edition, fetchImpl);
    pack[key] = texts;
    if (key === 'arabic') pack.surahs = surahs;
  }

  pack.bytes = approximatePackBytes(pack);
  await writePack(PACK_KEY, pack);
  onProgress({ step: steps.length, total: steps.length, label: 'Saved', done: true });
  return pack;
};

export const loadPack = async () => {
  if (!isSupported()) return null;
  try {
    const pack = await readPack(PACK_KEY);
    if (!pack || pack.version !== PACK_VERSION) return null;
    if (!Array.isArray(pack.arabic) || pack.arabic.length !== TOTAL_QURAN_VERSES) return null;
    return pack;
  } catch (error) {
    console.error('Could not read the offline text:', error);
    return null;
  }
};

export const deletePack = async () => {
  try {
    await removePack(PACK_KEY);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * One verse out of the pack, in the shape the reader expects.
 *
 * Returns null when the pack was downloaded for a different translation: the
 * stored text would simply be the wrong one, and quietly showing it would be
 * worse than going to the network.
 */
export const verseFromPack = (pack, surah, verse, translation) => {
  if (!pack || pack.editions.translation !== translation) return null;
  const index = globalAyahNumber(surah, verse);
  if (!index) return null;

  const surahInfo = (pack.surahs || []).find((entry) => entry.number === surah);

  return {
    arabic: pack.arabic[index - 1],
    translation: pack.translation[index - 1],
    transliteration: pack.transliteration[index - 1],
    surahInfo,
    translationEdition: { language: 'en' },
    audioUrl: '',        // no audio in this pack: that is a separate, far larger download
    fromOffline: true
  };
};

// The pack is read once and kept for the session: it is a few megabytes, and
// re-reading it on every verse would be wasteful.
let cachedPack = null;
let pendingLoad = null;

export const ensurePack = () => {
  if (cachedPack) return Promise.resolve(cachedPack);
  if (!pendingLoad) {
    pendingLoad = loadPack().then((pack) => {
      cachedPack = pack;
      pendingLoad = null;
      return pack;
    });
  }
  return pendingLoad;
};

export const setCachedPack = (pack) => { cachedPack = pack; };
